import React from 'react'
import PropTypes from 'prop-types'

import { some, isEqual, pick, throttle, get } from 'lodash'

import api from 'common/api'

import qs from '@scoir/query-string'

import RawGoogleMap, { withGoogleMapsApi } from 'components/GoogleMap'
import FillViewHeight from 'components/FillViewHeight'
import MapControlOverlay from './MapControlOverlay'

import { getIconForHighSchool } from './icon-helper'
import mapStyles from './mapStyles'
import clusterStyles from './cluster-styles'
import ExploreLayout from '../ExploreLayout'
import ExploreRail from '../ExploreRail'
import HighSchoolList from './HighSchoolList'

const MAP_OPTIONS = {
  minZoom: 6,
  styles: mapStyles,
  mapTypeControl: false,
  fullscreenControl: false,
}

const GOOGLE_MAPS_KEY = 'AIzaSyBrUOmema_7ysyJal6EUNqqzGUlAljiOjw'

const GoogleMap = withGoogleMapsApi(GOOGLE_MAPS_KEY)(RawGoogleMap)

const ON_MAP_CHANGE_LOAD_ZOOM = 7

export default class ExploreMap extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      highSchools: [],
      isLoading: false,
      hasSearchedManually: false,
      isManualSearchEnabled: false,
    }
    this.throttledLoadMap = throttle((filters) => this.loadHighSchoolsForMap(filters), 1000)
  }
  static propTypes = {
    zoom: PropTypes.number.isRequired,
    lat: PropTypes.number.isRequired,
    lng: PropTypes.number.isRequired,
    dataFilters: PropTypes.object.isRequired,
    onMapChange: PropTypes.func.isRequired,
    onHighSchoolClear: PropTypes.func.isRequired,
    onHighSchoolSelect: PropTypes.func.isRequired,
    selectedHighSchoolId: PropTypes.string,
  }
  componentDidMount() {
    document.title = 'Scoir - Explore Map'
    this.throttledLoadMap(this.props.dataFilters)
  }
  componentDidUpdate(prevProps) {
    const isApiQueryChanging = !isEqual(prevProps.dataFilters, this.props.dataFilters)
    const shouldAutoSearch = this.props.zoom > ON_MAP_CHANGE_LOAD_ZOOM
    const oldLocation = {
      lat: prevProps.lat,
      lng: prevProps.lng,
    }
    const newLocation = {
      lat: this.props.lat,
      lng: this.props.lng,
    }
    const hasZoomDecreased = this.props.zoom < prevProps.zoom
    const hasLocationChanged = !isEqual(oldLocation, newLocation)
    if (this.state.isLoading) {
      return
    }
    if (shouldAutoSearch && some([isApiQueryChanging, hasLocationChanged, hasZoomDecreased])) {
      this.throttledLoadMap(this.props.dataFilters)
    }
    if (prevProps.selectedHighSchoolId !== this.props.selectedHighSchoolId) {
      const oldMarker = this.mapInstance.getMarkerById(prevProps.selectedHighSchoolId)
      const newMarker = this.mapInstance.getMarkerById(this.props.selectedHighSchoolId)
      if (oldMarker) {
        oldMarker.setIcon(getIconForHighSchool(oldMarker.scoirSchool, oldMarker.hasScheduledVisit, false))
      }
      if (newMarker) {
        newMarker.setIcon(getIconForHighSchool(newMarker.scoirSchool, newMarker.hasScheduledVisit, true))
      }
    }

    if (isApiQueryChanging && !shouldAutoSearch) {
      this.setState({ isManualSearchEnabled: true })
    }
  }
  loadHighSchoolsForMap(filters, from = 0) {
    const query = qs.stringify({
      ...filters,
      map: true,
      from,
      limit: 2500,
    })
    this.setState({ isLoading: true })
    api
      .get(`/api/highschools/locations?${query}`)
      .then((result) => {
        const highSchools = get(result, 'HighSchoolExplores', [])
        this.setState({ highSchools })
      })
      .finally(() =>
        this.setState({
          hasSearchedManually: false,
          isManualSearchEnabled: false,
          isLoading: false,
        }),
      )
  }
  dragEnd(coords) {
    this.props.onMapChange({
      latitude: coords.lat,
      longitude: coords.lng,
    })
  }
  searchThisArea() {
    this.setState({ hasSearchedManually: true }, () => {
      this.throttledLoadMap(this.props.dataFilters)
    })
  }
  render() {
    const shouldAutoSearch = this.props.zoom > ON_MAP_CHANGE_LOAD_ZOOM
    return (
      <div className="route-explore-map">
        <ExploreLayout
          rail={
            <FillViewHeight>
              <ExploreRail
                selectedHighSchoolId={this.props.selectedHighSchoolId}
                onOverviewClose={this.props.onHighSchoolClear}
                defaultView={
                  <HighSchoolList
                    dataFilters={this.props.dataFilters}
                    onSelectHighSchool={(hs) => this.props.onHighSchoolSelect(hs.Id, hs.Latitude, hs.Longitude, true)}
                    shouldAutoSearch={shouldAutoSearch}
                    hasSearchedManually={this.state.hasSearchedManually}
                  />
                }
              />
            </FillViewHeight>
          }
          main={
            <FillViewHeight>
              <MapControlOverlay
                isLoading={this.state.isLoading}
                showManualSearch={!shouldAutoSearch && this.state.isManualSearchEnabled}
                onManualSearch={() => this.searchThisArea()}
                onAfterGoToDefault={() => this.mapInstance.exitStreetView()}
              />
              <GoogleMap
                instanceRef={(ref) => (this.mapInstance = ref)}
                center={pick(this.props, ['lat', 'lng'])}
                zoom={this.props.zoom}
                data={this.state.highSchools}
                dataId="HighSchoolDetail.HighSchoolId"
                onDragEnd={(coords) => this.dragEnd(coords)}
                onZoomChanged={(zoom, newCenter) => {
                  if (zoom !== this.props.zoom) {
                    this.props.onMapChange({
                      latitude: newCenter.lat,
                      longitude: newCenter.lng,
                      zoom,
                    })
                  }
                }}
                mapOptions={MAP_OPTIONS}
                selectedHighSchoolId={this.props.selectedHighSchoolId}
                onHighSchoolSelect={this.props.onHighSchoolSelect}
                cluster={this.props.zoom <= 10}
                clusterOptions={{
                  gridSize: 100,
                  styles: clusterStyles,
                }}
              />
            </FillViewHeight>
          }
        />
      </div>
    )
  }
}
